import { useContext, useState } from "react";
import { BlobServiceClient } from "@azure/storage-blob";
import { Notification } from "@cea/ui-kit";
import PropTypes from "prop-types";

import AuthContext from "../../contexts/AuthContext";
import FileUploadContext from "../../contexts/FileUploadContext";
import { getNotification } from "../../utils";
import formatFileSize from "../../utils/formatFileSize";

import { UploadNotificationContent } from "./UploadNotificationContent";

export const Status = {
  FILE_UPLOAD_START: <div className="font-semibold">File upload started</div>,
  FILE_UPLOAD_IN_PROGRESS: <div className="font-semibold">Uploading file</div>,
  FILE_UPLOAD_FINISH: <div className="font-semibold">Upload complete!</div>,
  FILE_UPLOAD_FAIL: <div className="font-semibold">Upload Failed!</div>,
};

const sasZipWrite = process.env.REACT_APP_AZURE_STORAGE_ZIP_CONTAINER_SAS_WRITE;
const zipContainerName = process.env.REACT_APP_AZURE_STORAGE_ZIP_CONTAINER;

const sasWrite = process.env.REACT_APP_SAS_WRITE;
const containerName = process.env.REACT_APP_AZURE_STORAGE_ADMIN_CONTAINER_NAME;

const blobServiceZipClientWrite = new BlobServiceClient(
  `${process.env.REACT_APP_STORAGE_URL}?${sasZipWrite}`
);

const blobServiceClientWrite = new BlobServiceClient(
  `${process.env.REACT_APP_STORAGE_URL}?${sasWrite}`
);

const FileUploadProvider = ({ children }) => {
  const [progress, setProgress] = useState({
    files: {},
  });

  const { token } = useContext(AuthContext);

  const [currentFileUploadJobId, setCurrentFileUploadJobId] = useState("");

  const addFileUploadJob = (newFileList) => {
    setProgress((prevState) => {
      const newFiles = { ...prevState.files };

      newFileList.forEach((fileObj) => {
        newFiles[fileObj.id] = {
          ...fileObj, // or an array if you're handling multiple per ID
          uploadStatus: null,
          uploadPercentComplete: 0,
          uploadError: null,
          isFileUploading: true,
        };
      });

      return {
        ...prevState,
        files: newFiles,
      };
    });
  };

  const updateFileUploadJob = (fileId, valuesToUpdate) => {
    setProgress((prevState) => {
      const newFileList = {
        ...prevState.files,
        fileId: {
          ...prevState.files[fileId],
          ...valuesToUpdate,
        },
      };

      return {
        ...prevState,
        files: newFileList,
      };
    });
  };

  const removeFileUploadJob = (fileId) => {
    setProgress((prevState) => {
      const { [fileId]: _, ...newFiles } = prevState.files;

      return {
        ...prevState,
        files: newFiles,
      };
    });
  };

  const showUploadProgressNotification = async ({
    parentId,
    serviceType,
    clientId,
    formValues,
    createFiles,
    metadata,
    onFileCreate,
  }) => {
    try {
      const updatedFileLinks = [];
      const files = Object.values(progress.files).filter(
        (fileObj) => fileObj.jobId === currentFileUploadJobId
      );

      const isZipFile = files[0].name.toLowerCase().endsWith(".zip");
      const totalSize = formatFileSize(
        files.reduce((sum, item) => sum + item.originalFileObj.size, 0)
      );

      setProgress((prevState) => ({ ...prevState, isFileUploading: true }));

      if (isZipFile) {
        await fileUploadToBlob({
          blobName: files[0].name,
          data: files[0].originalFileObj,
          jobId: files[0].jobId,
          totalSize,
          id: files[0].id,
        });

        fetch(process.env.REACT_APP_EXTRACTZIP_FUNC, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: token,
          },
          body: JSON.stringify({
            fileName: files[0].name,
            parentFolderId: parentId,
            serviceType: serviceType.toUpperCase(),
            ...metadata,
          }),
        });
      } else {
        for (const file of files) {
          const updatedFileName = `${new Date().getTime()}-${file.name}`;

          const newFile = new File([file.originalFileObj], updatedFileName, {
            type: file.originalFileObj.type,
          });

          updatedFileLinks.push({
            name: file.name,
            link: updatedFileName,
          });

          await fileUploadToBlob({
            blobName: updatedFileName,
            data: newFile,
            jobId: file.jobId,
            totalSize,
            id: file.id,
          });
        }

        await createFiles({
          parentId,
          serviceType,
          clientId,
          formValues: {
            ...formValues,
            link: updatedFileLinks,
          },
          ...metadata,
          onFileCreate,
        });
      }

      removeFileUploadJob(files[0].id);
    } catch (error) {
      const message = getNotification(error);

      Notification.error({
        message,
      });
    }
  };

  const fileUploadToBlob = async ({
    blobName,
    data,
    jobId,
    totalSize,
    fileId,
  }) => {
    const isZipFile = blobName.toLowerCase().endsWith(".zip");
    let containerClient;

    if (isZipFile) {
      containerClient =
        blobServiceZipClientWrite.getContainerClient(zipContainerName);
    } else {
      containerClient =
        blobServiceClientWrite.getContainerClient(containerName);
    }

    const blockBlobClient = containerClient.getBlockBlobClient(blobName);

    try {
      Notification.open({
        key: jobId,
        duration: 0,
        message: Status.FILE_UPLOAD_START,
        placement: "bottomRight",
        description: (
          <UploadNotificationContent
            fileName={blobName}
            fileSize={totalSize}
            progress={0}
          />
        ),
      });

      updateFileUploadJob(fileId, { uploadStatus: Status.FILE_UPLOAD_START });

      await blockBlobClient.uploadData(data, {
        blobHTTPHeaders: {
          blobContentType: isZipFile ? "application/zip" : data.type,
        },

        onProgress: (progress) => {
          const percentage = Math.round(
            (progress.loadedBytes / data.size) * 100
          );

          Notification.open({
            key: jobId,
            duration: 0,
            message: Status.FILE_UPLOAD_IN_PROGRESS,
            placement: "bottomRight",
            description: (
              <UploadNotificationContent
                fileName={blobName}
                fileSize={totalSize}
                progress={percentage}
              />
            ),
          });

          updateFileUploadJob(fileId, {
            uploadStatus: Status.FILE_UPLOAD_START,
            uploadPercentComplete: percentage,
          });
        },
      });

      Notification.open({
        key: jobId,
        duration: 0,
        message: Status.FILE_UPLOAD_FINISH,
        placement: "bottomRight",
        description: (
          <UploadNotificationContent
            fileName={blobName}
            fileSize={totalSize}
            progress={100}
          />
        ),
      });

      updateFileUploadJob(fileId, {
        uploadStatus: Status.FILE_UPLOAD_FINISH,
      });
    } catch (error) {
      Notification.open({
        key: jobId,
        duration: 0,
        message: Status.FILE_UPLOAD_FAIL,
        placement: "bottomRight",
        description: (
          <UploadNotificationContent
            fileName={blobName}
            fileSize={totalSize}
            progress={progress.fileUploadPercentComplete}
            uploadFailed
          />
        ),
      });

      updateFileUploadJob(fileId, {
        uploadStatus: Status.FILE_UPLOAD_FINISH,
        uploadError: error.message,
      });
    }
  };

  return (
    <FileUploadContext.Provider
      value={{
        ...progress,
        fileUploadToBlob,
        showUploadProgressNotification,
        setCurrentFileUploadJobId,
        currentFileUploadJobId,
        addFileUploadJob,
        removeFileUploadJob,
      }}
    >
      {children}
    </FileUploadContext.Provider>
  );
};

FileUploadProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default FileUploadProvider;
